import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { SITE_URL } from "@/config";
import { collections } from "@/mocks/collections";
import { CollectionCard } from "@/components/feature/cards";

const principles = [
  {
    n: "01",
    icon: "ri-focus-2-line",
    title: "整理，而不是炒作",
    desc: "我们做的是把散落的信息按主题与时间线归拢成实时吃瓜合集，帮你省去全网来回翻的力气，不靠标题煽动情绪。",
  },
  {
    n: "02",
    icon: "ri-scales-3-line",
    title: "观察，而不是下结论",
    desc: "信息未明的瓜，我们如实标注进展并持续蹲后续更新，把来龙去脉讲清楚，把判断权交还给你，不替任何人盖棺定论。",
  },
  {
    n: "03",
    icon: "ri-voiceprint-line",
    title: "真实，而不是人云亦云",
    desc: "我们引用可核对的公开信息并区分“事实”“传闻”与“观点”，不虚构公司资质、认证或权威奖项，杜绝编造与移花接木。",
  },
  {
    n: "04",
    icon: "ri-heart-3-line",
    title: "体面，而不是猎奇",
    desc: "娱乐可以很热闹，但不应越界到他人生活。我们关注作品、事件与现象本身，对真实个体的隐私与尊严保持应有的克制。",
  },
];

const voiceQuotes = [
  { text: "终于不用到处翻热搜了，一个合集全给整理明白。", who: "常读用户 · 阿茶" },
  { text: "立场中立的观察稿在吃瓜区真的稀缺，值得关注。", who: "读者留言 · 抹茶拿铁" },
  { text: "蹲后续是真的在蹲，进展都会持续更新，好评。", who: "老读者 · 一颗瓜籽" },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于吃瓜日报",
      url: `${SITE_URL}/about`,
      description: "吃瓜日报是关于实时吃瓜合集与娱乐话题盘点的内容平台介绍页。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "吃瓜日报",
      url: SITE_URL,
      description: "实时吃瓜平台，聚合全网热门大瓜、实时吃瓜合集与盘点整理的内容门户。",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "editorial",
        email: "hello@chigua-daily.example.com",
      },
    },
  ];

  return (
    <>
      <Seo
        title="关于吃瓜日报 - 我们的编辑理念 | 实时吃瓜"
        description="了解吃瓜日报的内容定位与编辑原则：整理实时吃瓜合集、客观盘点热门大瓜、尊重真实与隐私。欢迎来稿与反馈。"
        keywords="关于吃瓜日报,编辑理念,实时吃瓜,吃瓜日报"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />

      {/* Header */}
      <section className="border-b border-background-200 bg-gradient-to-b from-background-100/70 to-background-50">
        <div className="container-x py-12 md:py-16">
          <nav className="mb-6" aria-label="面包屑">
            <ol className="flex items-center gap-1.5 text-xs text-foreground-500">
              <li className="flex items-center gap-1.5">
                <Link to="/" className="transition-colors hover:text-primary-600">
                  首页
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-foreground-300" />
              </li>
              <li className="text-foreground-700">关于本站</li>
            </ol>
          </nav>
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
              <i className="ri-newspaper-line" /> About Chigua Daily
            </p>
            <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl md:leading-tight">
              把全网的大瓜，整理成一座清清爽爽的瓜田
            </h1>
            <p className="mt-5 text-base leading-relaxed text-foreground-700 md:text-lg">
              《吃瓜日报》是一个专注{" "}
              <strong className="font-bold text-foreground-900">实时吃瓜合集</strong>
              与 <strong className="font-bold text-foreground-900">大瓜盘点</strong>
              的内容站点。我们相信，追热点不该是一场信息灾难——它也可以是被理清楚、被尊重、值得回味的。
            </p>
          </div>
        </div>
      </section>

      {/* Positioning / three cards */}
      <section className="container-x py-10 md:py-14">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { icon: "ri-flashlight-fill", t: "为谁而做", d: "给爱刷热搜、追综艺、赶现场、关心内容行业的年轻人，提供省时的瓜料整理。", c: "bg-primary-100 text-primary-700" },
            { icon: "ri-compass-3-line", t: "我们提供什么", d: "实时热点动态、专题合集盘点、追更线索与内容产业观察，覆盖综艺、演出、短剧、饭圈等话题。", c: "bg-accent-100 text-accent-700" },
            { icon: "ri-leaf-line", t: "我们拒绝什么", d: "拒绝标题党、编造细节、消费真实个人隐私，也拒绝把吃瓜变成一场又一场网络审判。", c: "bg-secondary-100 text-secondary-800" },
          ].map((card) => (
            <div
              key={card.t}
              className="rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span
                className={
                  "flex h-11 w-11 items-center justify-center rounded-lg text-xl " + card.c
                }
              >
                <i className={card.icon} />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                {card.t}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="border-y border-background-200 bg-background-100/60">
        <div className="container-x py-10 md:py-14">
          <div className="mb-8 max-w-2xl">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-accent-700">
              <i className="ri-shield-star-line" /> 内容原则
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们是怎么“吃瓜”的
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {principles.map((p) => (
              <div
                key={p.n}
                className="group flex gap-5 rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="font-heading text-3xl font-black text-primary-500/30 transition-colors group-hover:text-primary-500">
                  {p.n}
                </span>
                <div>
                  <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className={`${p.icon} text-accent-600`} />
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voice */}
      <section className="container-x py-10 md:py-14">
        <div className="mb-8 max-w-2xl">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary-600">
            <i className="ri-chat-heart-line" /> 读者口碑
          </p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            常读我们的人这样说
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {voiceQuotes.map((q) => (
            <figure
              key={q.who}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <i className="text-2xl text-accent-400 ri-double-quotes-l" />
              <blockquote className="mt-3 flex-1 text-[15px] leading-relaxed text-foreground-800">
                {q.text}
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-2 text-sm font-bold text-foreground-600">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-200 text-xs text-secondary-900">
                  {q.who.slice(0, 1)}
                </span>
                {q.who}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Collections CTA */}
      <section className="border-t border-background-200 bg-background-100/60">
        <div className="container-x py-10 md:py-12">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-accent-700">
                <i className="ri-folder-open-line" /> 从哪儿开始吃
              </p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                挑一个你感兴趣的专题
              </h2>
            </div>
            <Link
              to="/collection/featured"
              className="inline-flex items-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-700"
            >
              进入全部合集 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {collections.slice(0, 3).map((c) => (
              <CollectionCard key={c.id} collection={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="container-x py-10 md:py-14">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-gradient-to-br from-secondary-100/50 to-background-50 p-6 md:p-10">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="max-w-xl">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                有选题想聊？欢迎来信
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-foreground-700">
                无论是投稿、纠错、选题建议，还是商务合作，都欢迎通过下方邮箱与我们联系。我们珍视每一份认真的反馈。
              </p>
              <div className="mt-5 flex flex-col gap-2.5 text-sm text-foreground-700 sm:flex-row sm:flex-wrap sm:gap-x-8">
                <span className="inline-flex items-center gap-2">
                  <i className="ri-mail-line text-accent-700" />
                  hello@chigua-daily.example.com
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="ri-time-line text-accent-700" />
                  工作日 10:00 - 18:00 回复
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="ri-global-line text-accent-700" />
                  线上编辑部 · 面向全网读者
                </span>
              </div>
            </div>
            <a
              href="#subscribe"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-add-line" /> 订阅周报，随时联系
            </a>
          </div>
        </div>
      </section>
    </>
  );
}